import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _extends from "@babel/runtime/helpers/esm/extends";
var _excluded = ["options", "defaultOption", "disabled", "disabledOptions", "invalid", "isCorrectInput", "isRestoreCode", "label", "locale", "onBlur", "onChange", "onOptionSort", "onFocus", "placeholder", "searchable", "size", "subText", "success", "theme", "tooltip", "tooltipAbsolute", "tooltipProps", "value", "isValidateOnBlur", "isReflectOptionValue", "noOptionsMessage", "getMaskMap", "getOptionMaker", "getValidator", "customSetter", "namespace", "inputProps", "classes", "fieldButton", "onChangeSelect", "onlyIconSelect", "selectDisabled", "controlledOption", "variant", "direction"];
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames/bind';
import { arrayOf, bool, func, node, object, oneOf, shape, string } from 'prop-types';
import { useMediaQuery } from "../hooks";
import { CheckLinear, ErrorCircleFilled } from "../icons";
import { ClickOutside } from "../internal/ClickOutside";
import { FieldIcon, FieldInput, FieldRoot, FieldSubText, FieldTooltip, useField } from "../internal/Field";
import { sizes, themes } from "../internal/Field/constants";
import { checkLocale } from "../utils/locale";
import noop from "../utils/noop";
import { isArray, isFunc } from "../utils/type";
import { getLocalByPhoneNumber } from '../PhoneField/helpers';
import Select from '../Select';
import { Control } from './components';
import { defaultSort, generateOption } from './helpers';
import styles from './SelectField.css';
var cx = classNames.bind(styles);
var COMPONENTS = {
  ValueContainer: noop,
  Control: Control,
  DropdownIndicator: noop,
  ClearIndicator: noop
};

var delay = function delay(ms) {
  return new Promise(function (resolve) {
    return setTimeout(resolve, ms);
  });
};

var getBasicOptionMaker = function getBasicOptionMaker() {
  var disabledOptions = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  var onlyIconSelect = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  var disabled = arguments.length > 2 ? arguments[2] : undefined;
  var selectDisabled = arguments.length > 3 ? arguments[3] : undefined;
  return function (_ref) {
    var value = _ref.value,
        label = _ref.label,
        pattern = _ref.pattern,
        icon = _ref.icon,
        _ref$iconProps = _ref.iconProps,
        iconProps = _ref$iconProps === void 0 ? {} : _ref$iconProps;
    var isDisabled = disabledOptions.map(function (item) {
      return item === null || item === void 0 ? void 0 : item.toLowerCase();
    }).includes(value === null || value === void 0 ? void 0 : value.toLowerCase());
    var Icon = icon;
    var iconColor = disabled || selectDisabled ? 'N60' : 'N300';
    return {
      left: Icon ? /*#__PURE__*/React.createElement(Icon, _extends({}, iconProps, {
        color: iconColor,
        className: cx('option_leftIcon', {
          option_disabled: isDisabled
        })
      })) : null,
      label: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
        className: cx('option_label', {
          option_disabled: isDisabled
        })
      }, !onlyIconSelect && label), /*#__PURE__*/React.createElement("span", {
        className: cx('option_value', {
          option_disabled: isDisabled
        })
      }, !onlyIconSelect && value)),
      name: label,
      value: value,
      isDisabled: isDisabled,
      validations: [pattern]
    };
  };
};

var checkPatterns = function checkPatterns(input, patterns) {
  var result = isArray(patterns) ? patterns.some(function (item) {
    return new RegExp(item).test(input);
  }) : false;
  return result;
};

var getBasicValidator = function getBasicValidator(_ref2) {
  var option = _ref2.option,
      setInputValue = _ref2.setInputValue,
      setIsValid = _ref2.setIsValid,
      onChange = _ref2.onChange,
      currentValue = _ref2.currentValue;

  if (currentValue && checkPatterns(currentValue, option === null || option === void 0 ? void 0 : option.validations)) {
    setIsValid(true);
  } else if (!option || currentValue && !checkPatterns(currentValue, option === null || option === void 0 ? void 0 : option.validations)) {
    setIsValid(false);
  }

  setInputValue(currentValue);
  onChange(currentValue);
};

var SelectField = function SelectField(_ref3) {
  var _cx;

  var options = _ref3.options,
      defaultOptionProp = _ref3.defaultOption,
      disabled = _ref3.disabled,
      disabledOptions = _ref3.disabledOptions,
      invalid = _ref3.invalid,
      isCorrectInput = _ref3.isCorrectInput,
      isRestoreCode = _ref3.isRestoreCode,
      label = _ref3.label,
      locale = _ref3.locale,
      onBlur = _ref3.onBlur,
      onChange = _ref3.onChange,
      onOptionSort = _ref3.onOptionSort,
      onFocus = _ref3.onFocus,
      placeholder = _ref3.placeholder,
      searchable = _ref3.searchable,
      size = _ref3.size,
      subText = _ref3.subText,
      success = _ref3.success,
      theme = _ref3.theme,
      tooltip = _ref3.tooltip,
      tooltipAbsolute = _ref3.tooltipAbsolute,
      tooltipProps = _ref3.tooltipProps,
      value = _ref3.value,
      _ref3$isValidateOnBlu = _ref3.isValidateOnBlur,
      isValidateOnBlur = _ref3$isValidateOnBlu === void 0 ? false : _ref3$isValidateOnBlu,
      _ref3$isReflectOption = _ref3.isReflectOptionValue,
      isReflectOptionValue = _ref3$isReflectOption === void 0 ? false : _ref3$isReflectOption,
      _noOptionsMessage = _ref3.noOptionsMessage,
      getMaskMap = _ref3.getMaskMap,
      getOptionMaker = _ref3.getOptionMaker,
      getValidator = _ref3.getValidator,
      customSetter = _ref3.customSetter,
      namespace = _ref3.namespace,
      inputProps = _ref3.inputProps,
      classes = _ref3.classes,
      fieldButton = _ref3.fieldButton,
      onChangeSelect = _ref3.onChangeSelect,
      onlyIconSelect = _ref3.onlyIconSelect,
      selectDisabled = _ref3.selectDisabled,
      controlledOption = _ref3.controlledOption,
      variant = _ref3.variant,
      direction = _ref3.direction,
      restProps = _objectWithoutProperties(_ref3, _excluded);

  var inputRef = useRef(null);
  var isTouched = useRef(false);
  var menuRef = useRef(null);
  var localeValue = checkLocale(locale);

  var _useField = useField({
    onBlur: onBlur,
    onFocus: onFocus,
    namespace: namespace,
    ref: inputRef,
    componentName: 'selectField'
  }),
      focused = _useField.focused,
      handleBlurHook = _useField.handleBlur,
      handleFocusHook = _useField.handleFocus,
      getTestId = _useField.getTestId,
      isAutofill = _useField.isAutofill,
      getNamespace = _useField.getNamespace;

  var defaultOption = getLocalByPhoneNumber(value) || defaultOptionProp;
  var prevDefaultOption = useRef(defaultOption); // eslint-disable-next-line react-hooks/exhaustive-deps

  var makeOption = useCallback(getOptionMaker(disabledOptions, onlyIconSelect, disabled, selectDisabled), [getOptionMaker, disabledOptions, onlyIconSelect, disabled, selectDisabled]);
  var optionList = useMemo(function () {
    return options;
  }, [options]);
  var maskMap = useMemo(function () {
    return getMaskMap ? getMaskMap(localeValue, makeOption) : null;
  }, [getMaskMap, localeValue, makeOption]);

  var _useState = useState(generateOption({
    maskMap: maskMap,
    optionList: optionList,
    makeOption: makeOption,
    value: defaultOption
  })),
      _useState2 = _slicedToArray(_useState, 2),
      option = _useState2[0],
      setOption = _useState2[1];

  useEffect(function () {
    if (controlledOption) {
      setOption(generateOption({
        maskMap: maskMap,
        optionList: optionList,
        makeOption: makeOption,
        value: controlledOption
      }));
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [controlledOption]);

  var _useState3 = useState(value !== null && value !== void 0 ? value : ''),
      _useState4 = _slicedToArray(_useState3, 2),
      inputValue = _useState4[0],
      setInputValue = _useState4[1];

  var _useState5 = useState(false),
      _useState6 = _slicedToArray(_useState5, 2),
      opened = _useState6[0],
      setOpened = _useState6[1];

  var _useState7 = useState(false),
      _useState8 = _slicedToArray(_useState7, 2),
      isValid = _useState8[0],
      setIsValid = _useState8[1];

  var _useState9 = useState(false),
      _useState10 = _slicedToArray(_useState9, 2),
      isInputFocused = _useState10[0],
      setIsInputFocused = _useState10[1];

  var _useState11 = useState([]),
      _useState12 = _slicedToArray(_useState11, 2),
      currentOptions = _useState12[0],
      setCurrentOptions = _useState12[1];

  var fieldButtonWrapper = useRef(null);
  var validateInput = useCallback(function (currentValue) {
    return getValidator({
      option: option,
      setOption: setOption,
      inputValue: inputValue,
      setInputValue: setInputValue,
      isValid: isValid,
      setIsValid: setIsValid,
      optionList: optionList,
      maskMap: maskMap,
      onChange: onChange,
      currentValue: currentValue
    });
  }, [getValidator, option, setOption, inputValue, setInputValue, isValid, setIsValid, optionList, maskMap, onChange]);
  var breakpoints = useMediaQuery('xs');
  var isMobile = breakpoints.xs;
  var callOnChange = useCallback(function () {
    var params = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    onChange(params === null || params === void 0 ? void 0 : params.value, false, {
      code: params === null || params === void 0 ? void 0 : params.code,
      countryCode: params === null || params === void 0 ? void 0 : params.country
    });
  }, [onChange]);
  useEffect(function () {
    var items = optionList.map(function (optionItem) {
      return maskMap ? maskMap[optionItem.value] : makeOption(optionItem);
    }).sort(onOptionSort || defaultSort);
    setCurrentOptions(items);
  }, [optionList, maskMap, onOptionSort, makeOption]);
  useEffect(function () {
    if (isInputFocused && !isTouched.current) {
      isTouched.current = true;
    }

    if (!isInputFocused && isTouched.current && option) {
      var _ref4, _option$value;

      if (!value || ((_ref4 = value || '') === null || _ref4 === void 0 ? void 0 : _ref4.length) < ((_option$value = option.value) === null || _option$value === void 0 ? void 0 : _option$value.length)) {
        if (isReflectOptionValue) {
          setInputValue(option.value);
        }
      }
    }
  }, [callOnChange, isInputFocused, isReflectOptionValue, option, value]);
  useEffect(function () {
    if (isReflectOptionValue) {
      setInputValue(value);
    }
  }, [isReflectOptionValue, value]);
  useEffect(function () {
    if (isFunc(customSetter) && option) {
      customSetter({
        maskMap: maskMap,
        option: option,
        value: value,
        setOption: setOption,
        setInputValue: setInputValue,
        defaultOption: defaultOption
      });
    }
  }, [customSetter, defaultOption, maskMap, option, value]);

  var handleToggleMenu = function handleToggleMenu() {
    if (!opened && inputRef.current) {
      inputRef.current.blur();

      if (isMobile) {
        delay(200).then(function () {
          return setOpened(!opened);
        });
        return;
      }
    }

    setOpened(!opened);
  };

  var handleCloseMenu = useCallback(function () {
    setOpened(false);
  }, []);

  var handleInputFocus = function handleInputFocus() {
    setIsInputFocused(true);
    handleFocusHook.apply(void 0, arguments);

    if (onFocus) {
      onFocus();
    }
  };

  var handleInputBlur = function handleInputBlur() {
    setIsInputFocused(false);

    if (prevDefaultOption.current && !option && isRestoreCode) {
      var optionDefault = generateOption({
        maskMap: maskMap,
        optionList: optionList,
        makeOption: makeOption,
        value: prevDefaultOption.current
      });
      callOnChange(optionDefault);
      setOption(optionDefault);
    }

    if (isValidateOnBlur) {
      validateInput();
    }

    handleBlurHook.apply(void 0, arguments);

    if (onBlur) {
      onBlur();
    }
  };

  var handleSetOption = function handleSetOption(val) {
    var _val$country;

    setOption(val);
    prevDefaultOption.current = val === null || val === void 0 ? void 0 : (_val$country = val.country) === null || _val$country === void 0 ? void 0 : _val$country.toLowerCase();
    setIsValid(false);
    inputRef.current.focus();

    if (isReflectOptionValue) {
      setInputValue(val.value);
      callOnChange(val);
    }

    if (opened) {
      setOpened(false);
    }

    if (onChangeSelect) {
      var optionValue = val.value;
      onChangeSelect(optionValue);
    }
  };

  useEffect(function () {
    if (variant === 'default') {
      setInputValue(value);
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [value]);

  var handleChange = function handleChange(e) {
    var currentValue = e.target.value;

    if (variant === 'default') {
      onChange(currentValue);
    } else if (!isValidateOnBlur) {
      validateInput(currentValue);
    } else {
      setInputValue(currentValue);
    }
  };

  var handleInputClick = function handleInputClick() {
    if (opened) {
      handleCloseMenu();
    }
  };

  var isSuccess = !invalid && (!!isCorrectInput || !isInputFocused && isValid);
  var ignoreRefs = useMemo(function () {
    return [menuRef, fieldButtonWrapper];
  }, []);
  return /*#__PURE__*/React.createElement(ClickOutside, _extends({}, restProps, getTestId(), {
    className: cx('wrapper', (_cx = {}, _defineProperty(_cx, "wrapper_size_".concat(size), size), _defineProperty(_cx, "wrapper_theme_".concat(theme), theme), _defineProperty(_cx, "wrapper_input_focused", isInputFocused), _defineProperty(_cx, "wrapper_invalid", invalid), _defineProperty(_cx, "wrapper_opened", opened && searchable), _cx), classes === null || classes === void 0 ? void 0 : classes.root),
    onClickOutside: isMobile ? noop : handleCloseMenu,
    ignoreRefs: ignoreRefs
  }), searchable && opened ? /*#__PURE__*/React.createElement(Select, {
    menuIsOpen: true,
    desktopMaxMenuHeight: 200,
    mobileMaxMenuHeight: 400,
    onMenuClose: handleCloseMenu,
    label: label,
    onSwipeDown: handleCloseMenu,
    onClickOverlay: handleCloseMenu,
    searchable: true,
    autoFocus: true,
    isDisabled: disabled || selectDisabled,
    closeMenuOnSelect: false,
    namespace: getNamespace(),
    menuRef: menuRef,
    onChange: handleSetOption,
    options: currentOptions,
    disabled: disabled || selectDisabled,
    isInputFocused: isInputFocused,
    placeholder: placeholder,
    noOptionsMessage: function noOptionsMessage() {
      return _noOptionsMessage;
    },
    dropdownIndicatorProps: {
      onClick: handleCloseMenu
    }
  }) : /*#__PURE__*/React.createElement("div", {
    className: cx('Select')
  }, /*#__PURE__*/React.createElement(Select, {
    components: COMPONENTS,
    menuIsOpen: opened,
    onTogglePhoneMenu: handleToggleMenu,
    onMenuClose: handleCloseMenu,
    desktopMaxMenuHeight: 200,
    mobileMaxMenuHeight: 400,
    label: label,
    onSwipeDown: handleCloseMenu,
    onClickOverlay: handleCloseMenu,
    menuVariant: 2,
    isDisabled: disabled || selectDisabled,
    namespace: getNamespace(),
    menuRef: menuRef,
    direction: direction,
    classes: {
      list: onlyIconSelect ? cx('only-icons') : ''
    },
    value: option,
    onChange: handleSetOption,
    options: currentOptions,
    disabled: disabled || selectDisabled,
    isInputFocused: isInputFocused,
    noOptionsMessage: function noOptionsMessage() {
      return _noOptionsMessage;
    }
  })), /*#__PURE__*/React.createElement("div", {
    className: cx('input')
  }, /*#__PURE__*/React.createElement(FieldRoot, {
    autofill: isAutofill,
    classes: {
      container: cx('input_container', fieldButton && 'input_container_with-button', classes === null || classes === void 0 ? void 0 : classes.container)
    },
    disabled: disabled,
    focused: focused || opened,
    invalid: invalid,
    size: size,
    success: !fieldButton && isSuccess,
    theme: theme
  }, /*#__PURE__*/React.createElement(FieldInput, _extends({}, inputProps, {
    ref: inputRef,
    disabled: disabled,
    invalid: invalid,
    onBlur: handleInputBlur,
    onChange: handleChange,
    onClick: handleInputClick,
    onFocus: handleInputFocus,
    label: label,
    value: inputValue,
    theme: theme,
    namespace: getNamespace(),
    classes: {
      root: classes === null || classes === void 0 ? void 0 : classes.inner,
      input: classes === null || classes === void 0 ? void 0 : classes.input,
      label: classes === null || classes === void 0 ? void 0 : classes.label
    }
  })), !fieldButton && isSuccess && /*#__PURE__*/React.createElement(FieldIcon, {
    invalid: invalid,
    theme: theme,
    position: "right",
    classes: {
      root: classes === null || classes === void 0 ? void 0 : classes.icon
    }
  }, /*#__PURE__*/React.createElement(CheckLinear, {
    size: "md"
  })), /*#__PURE__*/React.createElement("span", {
    ref: fieldButtonWrapper,
    "aria-hidden": "true"
  }, fieldButton))), tooltip && /*#__PURE__*/React.createElement(FieldTooltip, _extends({}, tooltipProps, {
    namespace: getNamespace(),
    absolute: tooltipAbsolute,
    invalid: invalid,
    success: success
  }), tooltip), subText && /*#__PURE__*/React.createElement(FieldSubText, {
    namespace: getNamespace(),
    invalid: invalid,
    errorIcon: /*#__PURE__*/React.createElement(ErrorCircleFilled, {
      size: "md"
    }),
    success: success
  }, subText));
};

export var locales = ['en', 'ru', 'it', 'es', 'de', 'zh'];
SelectField.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Classes API
   */
  classes: shape({
    root: string,
    container: string,
    inner: string,
    input: string,
    label: string,
    icon: string
  }),

  /**
   * Кастомный сеттер опций, с помощью него можно перехватить управление за выбором
   * опций. Например - можно управлять выбранной опцией в зависимости от ввода
   * в текстовом поле
   */
  customSetter: func,

  /**
   * Выбранная по умолчанию опция
   */
  defaultOption: string,

  /**
   * Инактивное состояние
   */
  disabled: bool,

  /**
   * Инактивные опции в выпадающем списке опций
   */
  disabledOptions: arrayOf(string),

  /**
   * Функция, которая возвращает объект с масками, которые затем применяются
   * к текстовому полю в зависимости от выбранной опции
   */
  getMaskMap: func,

  /**
   * Кастомные рендер опции
   */
  getOptionMaker: func,

  /**
   * Кастомный валидатор опций и ввода. С помощью него можно перехватить управление
   * за валидным состоянием компонента
   */
  getValidator: func,

  /**
   * Дополнительные параметры для **FieldInput**,
   * подробнее [тут](/story/intro-разработчикам-внутренние-компоненты--page#field)
   */
  inputProps: object,

  /**
   * Невалидное состояние
   */
  invalid: bool,

  /**
   * isCorrectInput
   */
  isCorrectInput: bool,
  // eslint-disable-line react/boolean-prop-naming

  /**
   * Подставлять значение выбранной опции в поле для ввода
   */
  isReflectOptionValue: bool,
  // eslint-disable-line react/boolean-prop-naming

  /**
   * Если указан defaultOption, опция зависит от поля ввода, и при некотором вводе
   * значение опции очищается, а после с поля для ввода убирает фокус (происходит blur),
   * значение опции восстанавливается к значение по умолчанию `defaultOption`.
   * Используется
   * в [PhoneField](/story/components-phonefield--playground&args=isRestoreCode:true)
   */
  isRestoreCode: bool,
  // eslint-disable-line react/boolean-prop-naming

  /**
   * Валидировать ввод при потере фокуса с поля для ввода (blur)
   */
  isValidateOnBlur: bool,
  // eslint-disable-line react/boolean-prop-naming

  /**
   * Плавающий плейсхолдер поля для ввода
   */
  label: node,

  /**
   * Локаль которая используется в `maskMap`
   */
  locale: string,

  /**
   * Уникальное пространство имен для атрибутов `data-test`
   */
  namespace: string,

  /**
   * Сообщение, отображаемое пи пустом результате поиска опций. Поиск опций
   * активируется параметром `searchable`
   */
  noOptionsMessage: node,

  /**
   * Обработчик `onBlur` поля для ввода
   */
  onBlur: func,

  /**
   * Обработчик `onChange` поля для ввода
   */
  onChange: func,

  /**
   * Обработчик `onFocus` поля для ввода
   */
  onFocus: func,

  /**
   * Кастомная сортировка опций выпадающего списка
   */
  onOptionSort: func,

  /**
   * Список опций для выпадающего списка
   */
  options: arrayOf(object),

  /**
   * Плейсхолдер для поискового поля, которое отображается когда включен параметр
   * `searchable`
   */
  placeholder: node,

  /**
   * Включить поиск по выпадающему списку
   */
  searchable: bool,

  /**
   * Размер
   */
  size: oneOf(sizes),

  /**
   * Уведомление под компонентом,
   * подробнее [тут](/story/intro-разработчикам-внутренние-компоненты--page#field)
   */
  subText: node,

  /**
   * Успешное состояние
   */
  success: bool,

  /**
   * Тема
   */
  theme: oneOf(themes),

  /**
   * Текст-подсказка под текстовым полем
   */
  tooltip: node,

  /**
   * Абсолютное позиционирование текста-подсказки
   */
  tooltipAbsolute: bool,

  /**
   * Дополнительные параметры для **FieldTooltip**,
   * подробнее [тут](/story/intro-разработчикам-внутренние-компоненты--page#field)
   */
  tooltipProps: object,

  /**
   * Значение текстового поля
   */
  value: string,

  /**
   * Кнопка справа, внутри input
   */
  fieldButton: node,

  /**
   * Callback на изменение селекта
   */
  onChangeSelect: func,

  /**
   * Свойство отвечающее за отображение только иконок в select
   */
  onlyIconSelect: bool,

  /**
   * Инактивное состояние select
   */
  selectDisabled: bool,

  /**
   * Значение Select
   */
  controlledOption: string,

  /**
   * Вариант использования SelectField
   */
  variant: oneOf(['phone', 'default']),
  direction: oneOf(['top', 'bottom'])
} : {};
SelectField.defaultProps = {
  label: '',
  theme: 'b2c',
  placeholder: 'Option value',
  noOptionsMessage: 'No options',
  getOptionMaker: getBasicOptionMaker,
  getValidator: getBasicValidator,
  size: 'medium',
  value: '',
  variant: 'default',
  direction: 'bottom'
};
export default SelectField;